import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ExternalLink, Github, ArrowUpRight } from "lucide-react";
import { projects } from "../data/portfolio";

function ProjectCard({ project, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 36 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: index * 0.12, ease: [0.23, 1, 0.32, 1] }}
      className="glass-card project-card-line relative p-8 flex flex-col h-full group overflow-hidden"
    >
      {/* Background glow on hover */}
      <div
        className="absolute -top-20 -right-20 w-48 h-48 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none blur-3xl"
        style={{ background: `radial-gradient(circle, ${project.accentColor}18, transparent 70%)` }}
      />

      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <p
            className="font-mono text-[11px] tracking-[0.18em] uppercase mb-2"
            style={{ color: project.accentColor }}
          >
            {project.type}
          </p>
          <h3 className="font-display font-black text-2xl tracking-tight text-white">
            {project.title}
          </h3>
        </div>
        <div className="flex gap-2 shrink-0">
          <a
            href={project.githubUrl}
            target="_blank"
            rel="noreferrer"
            data-hover
            aria-label="GitHub"
            className="flex items-center justify-center w-8 h-8 rounded-md border border-white/[0.08] text-white/30 hover:text-white hover:border-white/20 transition-all duration-200"
          >
            <Github size={14} />
          </a>
          <a
            href={project.liveUrl}
            target="_blank"
            rel="noreferrer"
            data-hover
            aria-label="Live"
            className="flex items-center justify-center w-8 h-8 rounded-md border border-white/[0.08] text-white/30 hover:text-white hover:border-white/20 transition-all duration-200"
          >
            <ExternalLink size={14} />
          </a>
        </div>
      </div>

      {/* Description */}
      <p className="text-white/45 text-sm leading-relaxed mb-6 flex-1">
        {project.description}
      </p>

      {/* Highlights */}
      <ul className="mb-6 space-y-1.5">
        {project.highlights.map((h) => (
          <li key={h} className="flex items-start gap-2 text-xs text-white/35 font-mono">
            <span style={{ color: project.accentColor }} className="mt-0.5 shrink-0">→</span>
            {h}
          </li>
        ))}
      </ul>

      {/* Tech stack */}
      <div className="flex flex-wrap gap-1.5 mb-6">
        {project.tech.map((t) => (
          <span
            key={t}
            className="px-2.5 py-1 rounded-full text-[11px] font-mono text-white/40 border border-white/[0.07]"
          >
            {t}
          </span>
        ))}
      </div>

      {/* Footer links */}
      <div className="flex gap-4 pt-4 border-t border-white/[0.06]">
        <a
          href={project.liveUrl}
          target="_blank"
          rel="noreferrer"
          data-hover
          className="flex items-center gap-1.5 text-xs font-semibold transition-all duration-200 hover:-translate-y-0.5"
          style={{ color: project.accentColor }}
        >
          <span>Live Demo</span>
          <ArrowUpRight size={12} />
        </a>
        <a
          href={project.githubUrl}
          target="_blank"
          rel="noreferrer"
          data-hover
          className="flex items-center gap-1.5 text-xs font-semibold text-white/35 hover:text-white transition-all duration-200 hover:-translate-y-0.5"
        >
          <Github size={12} />
          <span>Source Code</span>
        </a>
      </div>
    </motion.div>
  );
}

export default function Projects() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section id="projects" className="relative px-6 md:px-16 lg:px-24 py-32">
      <div className="max-w-6xl mx-auto">
        <div ref={ref}>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="section-label mb-3"
          >
            <span className="font-mono text-xs tracking-[0.2em] uppercase text-blue-400">03 // Projects</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 28 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.23, 1, 0.32, 1] }}
            className="font-display font-black text-4xl md:text-5xl tracking-tight mb-4"
          >
            Things I've<br />
            <span className="grad-text">built.</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-white/40 text-base max-w-xl mb-16"
          >
            A selection of projects that reflect my engineering depth, design sensibility, and love for hard problems.
          </motion.p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {projects.map((project, i) => (
            <ProjectCard key={project.id} project={project} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
